import { useEffect, useRef } from 'react'

export interface CompanionChatRequest {
  id: string
  message?: string
  sessionId?: string
}

interface CompanionChatRequestOptions {
  request?: CompanionChatRequest | null
  enabled: boolean
  ownerKey: string
  sessionsReady: boolean
  historyLoading: boolean
  busy: boolean
  dailyLimited: boolean
  selectedSessionId?: string
  selectSession: (sessionId: string | undefined) => void
  prefill: (message: string) => void
  send: (message: string) => Promise<unknown>
  onHandled?: () => void
}

/** A companion submit is an explicit handoff; ordinary pendingPrompt stays a
 * draft. Delay dispatch one task so StrictMode's effect replay cannot send and
 * immediately abort the same request, and claim each request before sending. */
export function useCompanionChatRequest(options: CompanionChatRequestOptions) {
  const handlers = useRef(options)
  handlers.current = options
  const seen = useRef(new Map<string, { owner: string; handled: boolean }>())
  const {
    request,
    enabled,
    ownerKey,
    sessionsReady,
    historyLoading,
    busy,
    dailyLimited,
    selectedSessionId,
  } = options

  useEffect(() => {
    if (!enabled || !request || !ownerKey) return undefined
    const entry = seen.current.get(request.id) ?? {
      owner: ownerKey,
      handled: false,
    }
    seen.current.set(request.id, entry)
    if (entry.handled || entry.owner !== ownerKey || busy || !sessionsReady)
      return undefined

    const timer = window.setTimeout(() => {
      const current = handlers.current
      if (
        current.busy ||
        current.ownerKey !== ownerKey ||
        current.request?.id !== request.id
      )
        return
      if (selectedSessionId !== request.sessionId) {
        current.selectSession(request.sessionId)
        return
      }
      if (historyLoading) return

      entry.handled = true
      const message = request.message?.trim()
      if (message) {
        // Keep the text if a guard (credits, daily cap, or a racing manual
        // send) declines dispatch. A failed handoff must never eat the ask.
        current.prefill(message)
        if (!dailyLimited) void current.send(message)
      }
      current.onHandled?.()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [
    request,
    enabled,
    ownerKey,
    sessionsReady,
    historyLoading,
    busy,
    dailyLimited,
    selectedSessionId,
  ])
}
